/**
 * Payment Success Page
 * Shown after successful Stripe checkout for tourist rides
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { CheckCircle, Calendar, MapPin, Plane, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState(null);
  const [rideDetails, setRideDetails] = useState(null);

  const sessionId = searchParams.get('session_id');
  const rideId = searchParams.get('ride_id');

  useEffect(() => {
    if (sessionId) {
      verifyPayment();
    } else {
      setVerifying(false);
      setError('Invalid payment session');
    }
  }, [sessionId]);

  const verifyPayment = async () => {
    try {
      const response = await fetch(`${API_URL}/api/tourist/payment/verify/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();

      if (data.success && data.paid) {
        setVerified(true);
        // Fetch ride details
        fetchRideDetails(data.ride_id || rideId);
      } else {
        setError('Payment verification failed');
      }
    } catch (err) {
      setError('Failed to verify payment');
    } finally {
      setVerifying(false);
    }
  };

  const fetchRideDetails = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/rides/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setRideDetails(data.ride);
      }
    } catch (err) {
      console.error('Failed to fetch ride details:', err);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-4 border-green-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-600">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-red-50 to-white flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">⚠️</span>
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Payment Issue</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/dashboard')} className="w-full">
            Return to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-12 h-12 text-green-600" />
        </div>
        
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Payment Successful!</h2>
        <p className="text-gray-600 mb-6">Your airport transfer has been confirmed</p>

        {rideDetails && (
          <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left space-y-3">
            <div className="flex items-center gap-3">
              <Plane className="w-5 h-5 text-amber-600" />
              <div>
                <p className="text-sm text-gray-500">Pickup</p>
                <p className="font-medium">{rideDetails.airport_code} Airport</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <MapPin className="w-5 h-5 text-emerald-600" />
              <div>
                <p className="text-sm text-gray-500">Destination</p>
                <p className="font-medium">{rideDetails.destination_name}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-sm text-gray-500">Scheduled Time</p>
                <p className="font-medium">
                  {new Date(rideDetails.scheduled_time).toLocaleString('en-JM', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>

            <div className="pt-3 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Amount Paid</span>
                <span className="text-2xl font-bold text-green-600">
                  ${rideDetails.flat_rate_usd} USD
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <Button
            onClick={() => navigate(`/ride/track/${rideId}`)}
            className="w-full bg-green-600 hover:bg-green-700"
          >
            <span className="flex items-center gap-2">
              Track Your Ride
              <ArrowRight className="w-4 h-4" />
            </span>
          </Button>
          
          <Button
            onClick={() => navigate('/dashboard')}
            variant="outline"
            className="w-full"
          >
            Return to Dashboard
          </Button>
        </div>

        <p className="text-xs text-gray-500 mt-6">
          A driver will be assigned before your scheduled pickup time.
          You'll receive notifications with driver details.
        </p>
      </div>
    </div>
  );
};

export default PaymentSuccess;
